/* eslint-disable complexity */
import { addDays } from 'date-fns';
import isTest from './helpers/isTest';
import utils from './utils';
import context from './context';
import extendUserModel from './model-extensions/extendUserModel';
import extendShiftModel from './model-extensions/extendShiftModel';
import removeDeletedTaskTemplates from './helpers/shiftTasks/removeDeletedTaskTemplates';
import gettext from './lang/gettext';
import {
  RRULE_INTERVAL_THIS_WEEK,
  BUSINESS_CLOSED_TYPE,
  SUPPRESSION_TYPE,
  HOLIDAY_RATE_TYPE,
  EVENT_STATUS,
  NO_TAG_FILTER_OPTION_ID,
  NO_GROUP_FILTER_OPTION_ID,
  OTHER_DAY_PART_FILTER_OPTION_ID,
  FILTER_SUITES,
  SHIFT_TYPE,
} from './constants';

export default function CalendarCache(initialContext) {
  const instance = this;

  const cacheContext = initialContext;
  const _onChangeHandlers = [];
  const _onCurrentShiftsChangeHandlers = [];
  const _range = {};
  const _subscribedEpochs = {};
  const _logCache = {};
  let _leaveStatusCache = {};
  let _userLeaveCapsCache = {};
  let _notesDict = null;
  let _redoTransactionId = null;
  let _calendarSocketRoom = null;
  let _specialEventCache = {};
  let _shiftsCosts = {};
  let _shiftTempaltesPromise;

  if (!cacheContext.syncWorker) {
    cacheContext.syncWorker = () => Promise.resolve();
  }

  const specialEvents = {
    leaveSuppressions: SUPPRESSION_TYPE,
    businessClosedEvents: BUSINESS_CLOSED_TYPE,
    holidayRateEvents: HOLIDAY_RATE_TYPE,
  };

  Object.entries(specialEvents).forEach(([key, type]) => {
    Object.defineProperty(instance, key, {
      get() {
        if (!(type in _specialEventCache) && instance.allEvents) {
          _specialEventCache[type] = {};
          instance.allEvents.forEach((event) => {
            if (event.type === type) {
              const dateString = utils.formatDate(utils.getRealDate(event.dtstart));
              _specialEventCache[type][dateString] = event;
            }
          });
        }

        return _specialEventCache[type] || {};
      },
    });
  });

  instance.shiftTemplates = [];
  syncShiftTemplates();

  instance.undoingPossible = false;
  instance.staleLaborData = false;
  instance.timeOffTypes = [];
  instance.salaryCosts = {};
  instance.allSummaries = {};

  instance.setContext = setContext;
  instance.getEvents = getEvents;
  instance.getShiftsCosts = getShiftsCosts;
  instance.getSummaries = getSummaries;
  instance.onChange = onChange;
  instance.addOrEditEvents = addOrEditEvents;
  instance.deleteEvent = deleteEvent;
  instance.addOrEditNote = addOrEditNote;
  instance.deleteNote = deleteNote;
  instance.getLogs = getLogs;
  instance.updateCurrentShifts = updateCurrentShifts;
  instance.onCurrentShiftsChange = onCurrentShiftsChange;

  instance.addShift = addShift;
  instance.updateShift = updateShift;
  instance.shiftsBulk = shiftsBulk;
  instance.addTimeoff = addTimeoff;
  instance.updateTimeoff = updateTimeoff;
  instance.applyForShift = applyForShift;
  instance.deleteShift = deleteShift;
  instance.deleteShifts = deleteShifts;
  instance.publishShift = publishShift;
  instance.publishShifts = publishShifts;
  instance.offerShift = offerShift;
  instance.cancelShiftAvailability = cancelShiftAvailability;
  instance.getShiftCoworkers = getShiftCoworkers;
  instance.getShiftDetails = getShiftDetails;
  instance.makeShiftAvailable = makeShiftAvailable;
  instance.makeShiftsAvailable = makeShiftsAvailable;
  instance.acceptShiftOffer = acceptShiftOffer;
  instance.rejectShiftOffer = rejectShiftOffer;
  instance.approveShiftOffer = approveShiftOffer;
  instance.retractShiftOffer = retractShiftOffer;
  instance.cancelShiftOffer = cancelShiftOffer;
  instance.denyShiftApplicant = denyShiftApplicant;
  instance.denyShiftApplicants = denyShiftApplicants;
  instance.retractShiftApplication = retractShiftApplication;
  instance.approveShiftApplicant = approveShiftApplicant;
  instance.approveShiftApplications = approveShiftApplications;
  instance.copyShifts = copyShifts;
  instance.checkDirty = checkDirty;
  instance.checkUndoRedo = checkUndoRedo;
  instance.undoChanges = undoChanges;
  instance.redoChanges = redoChanges;
  instance.getNextShifts = getNextShifts;
  instance.getCurrentShifts = getCurrentShifts;
  instance.getClockIn = getClockIn;
  instance.getLastClockIn = getLastClockIn;
  instance.clockIn = clockIn;
  instance.clockOut = clockOut;
  instance.takeBreak = takeBreak;
  instance.endBreak = endBreak;
  instance.getEvent = getEvent;
  instance.getTimesheetEvent = getTimesheetEvent;
  instance.saveTimeOff = saveTimeOff;
  instance.deleteTimeOff = deleteTimeOff;
  instance.approveTimeOff = approveTimeOff;
  instance.denyTimeOff = denyTimeOff;
  instance.getTimeOffTypes = getTimeOffTypes;
  instance.saveTimeOffTypes = saveTimeOffTypes;
  instance.getUserLeavesStatus = getUserLeavesStatus;
  instance.getUserCaps = getUserCaps;
  instance.updateUserCaps = updateUserCaps;
  instance.syncUserLeaveCaps = syncUserLeaveCaps;
  instance.getNoShowSummary = getNoShowSummary;
  instance.getSickCalloutSummary = getSickCalloutSummary;
  instance.updateCalendarSocketRoom = updateCalendarSocketRoom;
  instance.addNoShow = addNoShow;
  instance.addSickCallout = addSickCallout;
  instance.deleteNoShow = deleteNoShow;
  instance.deleteSickCallout = deleteSickCallout;
  instance.markDay = markDay;
  instance.unmarkDay = unmarkDay;
  instance.markDayBulk = markDayBulk;
  instance.addBusinessClosedEvent = addBusinessClosedEvent;
  instance.deleteBusinessClosedEvent = deleteBusinessClosedEvent;
  instance.loadShiftsAvailableForSwap = loadShiftsAvailableForSwap;
  instance.initiateSwap = initiateSwap;
  instance.loadSwapDetails = loadSwapDetails;
  instance.cancelShiftSwap = cancelShiftSwap;
  instance.approveShiftSwap = approveShiftSwap;

  instance.addShiftTemplate = addShiftTemplate;
  instance.getShiftTemplates = getShiftTemplates;
  instance.updateShiftTemplate = updateShiftTemplate;
  instance.deleteShiftTemplate = deleteShiftTemplate;
  instance.syncShiftTemplates = syncShiftTemplates;

  instance.clearData = _clearData;
  instance.syncEvents = syncEvents;
  instance.syncLabor = syncLabor;
  instance.range = _range;

  instance.acceptShift = acceptShift;
  instance.rejectShift = rejectShift;
  instance.bulkAcceptShifts = bulkAcceptShifts;
  instance.addCalendarFiltersToQueryParams = addCalendarFiltersToQueryParams;

  instance._mergeUsers = _mergeUsers;
  instance._notifyOnChange = _notifyOnChange;

  /**
   * We cannot call an async function at that point
   * due to some hacks on web with async/await implementation
   */
  _resetData();

  Object.defineProperty(instance, 'notesDict', {
    get() {
      if (_notesDict === null) {
        _notesDict = {};

        if (instance.notes) {
          instance.notes.forEach((n) => {
            if (!_notesDict[n.date]) {
              _notesDict[n.date] = [];
            }

            _notesDict[n.date].push(n);
          });
        }
      }

      return _notesDict;
    },
  });

  Object.defineProperties(instance, {
    redoTransactionId: {
      get() {
        return _redoTransactionId;
      },
    },
  });

  function _resetData() {
    instance.allEvents = null;
    instance.allUsers = {};
    instance.notes = [];
    instance.lastSeenDate = null;
    instance.currentShifts = null;
    instance.notesPromise = null;
    instance.staleLaborData = false;
    instance.allSummaries = {};

    _shiftsCosts = {};
    instance.salaryCosts = {};
    _notesDict = null;
    _range.from = null;
    _range.to = null;
    _specialEventCache = {};
    utils.clearObject(_subscribedEpochs);
  }

  async function _clearData() {
    _resetData();
    await cacheContext.syncWorker({ clear: true });
  }

  async function _loadNotes(datesParam, ignoreLoadingBar) {
    instance.notesPromise = cacheContext.httpGet(
      `shifts/notes?dates=${encodeURIComponent(datesParam)}&user-fields=id`,
      undefined,
      { ignoreLoadingBar }
    );
    const data = await instance.notesPromise;

    instance.notes = instance.notes || [];

    data.forEach((note) => {
      instance.notes.push(note);
    });

    _notesDict = null;
  }

  function storeServerSideShiftsLaborData(events) {
    if (context.shouldProcessServerLaborData) {
      utils.storeServerSideShiftsLaborData(events, _shiftsCosts);
    }
  }

  async function processServerLaborData(events, shiftsCosts, fromDate, toDate) {
    if (context.shouldProcessServerLaborData && shiftsCosts) {
      const { costsArrays: costs, stale, salaries } = shiftsCosts;
      instance.staleLaborData = stale;
      Object.assign(_shiftsCosts, costs);
      updateSalaryCosts(salaries, fromDate, toDate);
      storeServerSideShiftsLaborData(events);
    }
  }

  async function _loadFromServer({
    fromDate,
    toDate,
    filters,
    ignoreLoadingBar = false,
    skipUnscheduled = false,
    showPlanningEvents = true,
    showForeignEvents = false,
    cleanCache = false,
  }) {
    const datesParam = utils.getDatesRangeParam(fromDate, toDate);

    // Load the day notes, asynchronous
    _loadNotes(datesParam, ignoreLoadingBar);

    const queryParams = {
      dates: datesParam,
      'user-fields': 'id',
    };

    if (!showForeignEvents && context.serverSideFilteringEnabled) {
      Object.assign(queryParams, {
        skipUnscheduled,
        showPlanningEvents,
      });

      addCalendarFiltersToQueryParams(queryParams, filters);
    }

    let [events, shiftsCosts] = await Promise.all([
      cacheContext.httpGet(
        `calendar/${context.orgId}/users/${context.me.id}${utils.resolveQueryParams(queryParams)}`,
        undefined,
        { ignoreLoadingBar }
      ),
      context.shouldProcessServerLaborData ? getShiftsCosts(fromDate, toDate) : null,
    ]);

    processServerLaborData(events, shiftsCosts, fromDate, toDate);

    events.forEach(({ user }) => {
      if (user && !(user.id in instance.allUsers)) {
        instance.allUsers[user.id] = extendUserModel(user);
      }
    });

    if (context.serverSideFilteringEnabled) {
      instance.allEvents = events;
    } else {
      if (cleanCache || !instance.allEvents) {
        instance.allEvents = [];
      }

      events.forEach((event) => {
        const existing = instance.allEvents.find((e) => e.id === event.id);
        if (!existing) {
          instance.allEvents.push(event);
        } else {
          Object.assign(existing, event, { EVENT_EXTENDED: false });
        }
      });
    }

    _specialEventCache = {};
    await cacheContext.syncWorker({ changed: events });
    return events;
  }

  function addCalendarFiltersToQueryParams(queryParams, filters) {
    if (!filters) {
      return;
    }

    if (filters.locationIds?.length) {
      queryParams.locationIds = filters.locationIds;
    }

    if (filters.positionIds?.length) {
      queryParams.positionIds = filters.positionIds;
    }

    if (filters.groupIds?.length) {
      const includeGroupIds = filters.groupIds.filter((id) => id !== NO_GROUP_FILTER_OPTION_ID);
      queryParams.groupIds = includeGroupIds;

      if (filters.groupIds.find((id) => id === NO_GROUP_FILTER_OPTION_ID)) {
        queryParams.excludeGroupIds = context.orgGroups
          .filter((g) => !includeGroupIds.includes(g.id))
          .map((g) => g.id);
      }
    }

    if (filters.userIds?.length) {
      queryParams.userIds = filters.userIds;
    }

    if (filters.tagIds?.length) {
      const includeTagIds = filters.tagIds.filter((id) => id !== NO_TAG_FILTER_OPTION_ID);
      queryParams.tagIds = includeTagIds;

      if (filters.tagIds.find((id) => id === NO_TAG_FILTER_OPTION_ID)) {
        queryParams.excludeTagIds = context.tags
          .filter((t) => !includeTagIds.includes(t.id))
          .map((t) => t.id);
      }
    }

    if (filters.dayPartIds?.length) {
      const includeDayPartIds = filters.dayPartIds.filter(
        (id) => id !== OTHER_DAY_PART_FILTER_OPTION_ID
      );
      queryParams.dayPartIds = includeDayPartIds;

      if (filters.dayPartIds.find((id) => id === OTHER_DAY_PART_FILTER_OPTION_ID)) {
        queryParams.excludeDayPartIds = context.dayParts
          .filter((dp) => !includeDayPartIds.includes(dp.id))
          .map((dp) => dp.id);
      }
    }

    if (filters.eventTypes?.length) {
      queryParams.eventTypes = filters.eventTypes;
    }

    if (filters.suite === FILTER_SUITES.availability) {
      queryParams.eventTypes = [...(queryParams.eventTypes || []), 'availability'];
    }

    if (filters.suite === FILTER_SUITES.mylocations) {
      queryParams.locationIds = (context.me.locationIds || []).filter(
        (id) => !queryParams.locationIds?.length || queryParams.locationIds.includes(id)
      );
    }

    if (filters.suite === FILTER_SUITES.myschedule) {
      queryParams.userIds = [context.me.id];
    }
  }

  function _notifyOnChange(...args) {
    _specialEventCache = {};
    _onChangeHandlers.forEach((h) => h(...args));
  }

  function _mergeUsers() {
    if (instance.allUsers) {
      context.orgUsers.forEach((user) => {
        instance.allUsers[user.id] = user;
      });

      const orgUsersIds = context.orgAllUsers.map((u) => String(u.id));
      // if a user got deleted in the meantime
      Object.keys(instance.allUsers).forEach((userId) => {
        const user = instance.allUsers[userId];
        if (!orgUsersIds.includes(String(userId))) {
          user.deleted = true;
        }
      });
    }
  }

  async function _addOrEditResponse(response) {
    let events = response;

    if ('events' in events) {
      events = events.events;
    }

    if (!Array.isArray(events)) {
      events = [events];
    }

    await instance.addOrEditEvents(events);
    return response;
  }

  function _extendParamsWithDates(params) {
    const { viewDatesParam } = context;
    const copy = { ...params };
    const cachedates =
      _range.from && _range.to ? utils.getDatesRangeParam(_range.from, _range.to) : undefined;

    if (context.isPrefab) {
      if (viewDatesParam) {
        const result = {
          ...copy,
          viewdates: viewDatesParam,
        };

        if (cachedates) {
          result.cachedates = cachedates;
        }

        return result;
      }
    } else if (_range.from && _range.to && cachedates) {
      return {
        ...copy,
        dates: cachedates,
      };
    }

    return copy;
  }

  function _transformToRepeatThisWeek(shift) {
    if (!shift.id) {
      const { firstDayOfWeek } = context;

      const from =
        shift.dtstart.getDay() === firstDayOfWeek
          ? shift.dtstart
          : utils.moveToDayOfWeek(shift.dtstart, firstDayOfWeek, -1);
      const to = utils.moveToDayOfWeek(shift.dtstart, firstDayOfWeek, 1);

      shift.dtend = addDays(shift.dtend, (from - shift.dtstart) / 1000 / 60 / 60 / 24);
      shift.dtstart = from;
      shift.rrule.from = `${utils.formatDate(from)}T00:00`;
      shift.rrule.until = `${utils.formatDate(addDays(to, -1))}T23:59`;
    }
  }

  function _transformShiftToServer(_shift, publish) {
    const shift = { ..._shift };
    delete shift.fullDay;

    if (shift.users) {
      if (!shift.id) {
        // adding a new shift
        // Post array of users, NOT the single user
        delete shift.user;
      } else {
        // Post single user
        if (shift.users.length) {
          shift.user = shift.users[0];
        } else {
          shift.user = null;
        }

        delete shift.users;
      }
    }

    if (shift.rrule) {
      if (shift.rrule.interval > 0) {
        shift.rrule.freq = 'WEEKLY';

        if (shift.rrule.interval === RRULE_INTERVAL_THIS_WEEK) {
          _transformToRepeatThisWeek(shift);
        } else if (shift.rrule.untilDate) {
          const untilDate = utils.getRealDate(shift.rrule.untilDate);
          const time = shift.rrule.untilChanged
            ? '23:59'
            : utils.formatTime(untilDate, false, true);
          shift.rrule.until = `${utils.formatDate(untilDate)}T${time}`;
          shift.rrule.untilDate = utils.getRealDate(shift.rrule.until.substr(0, 19));
        } else if (shift.rrule.untilDate === null) {
          shift.rrule.until = null;
        }

        delete shift.rrule.untilChanged;
      } else {
        delete shift.rrule;
      }
    }

    const dtstart = utils.getRealDate(shift.dtstart);
    const dtend = utils.getRealDate(shift.dtend);

    // Send timezone-less times!
    shift.dtstart = `${utils.formatDate(dtstart)}T${utils.formatTime(dtstart, false, true)}`;
    shift.dtend = `${utils.formatDate(dtend)}T${utils.formatTime(dtend, false, true)}`;

    if ('location' in shift) {
      shift.location = { id: shift.location.id };
    }

    if ('position' in shift) {
      shift.position = { id: shift.position.id };
    }

    if (publish !== undefined && shift.status !== EVENT_STATUS.CANCELLED) {
      shift.status = publish ? EVENT_STATUS.PUBLISHED : EVENT_STATUS.PLANNING;
    }

    if (!context.isOrgFeatureEnabled('tags')) {
      delete shift.tags;
    }

    if (!context.isOrgFeatureEnabled('shiftTasks')) {
      delete shift.taskTemplates;
    }

    delete shift.availableSlots; // remove deprecated field

    return shift;
  }

  function _transformLeaveToServer(_model) {
    const model = { ..._model };
    if (model.users) {
      if (model.users.length) {
        model.user = model.users[0];
        model.userId = model.users[0].id;
      } else {
        model.user = null;
        model.userId = 0;
      }

      delete model.users;
    }

    const dtstart = model.fullDay && model.fullDayDtstart ? model.fullDayDtstart : model.dtstart;
    const dtend = model.fullDay && model.fullDayDtstart ? model.fullDayDtend : model.dtend;

    // Send timezone-less times!
    model.dtstart = `${utils.formatDate(dtstart)}T${utils.formatTime(dtstart, false, true)}`;
    model.dtend = `${utils.formatDate(dtend)}T${utils.formatTime(dtend, false, true)}`;

    return model;
  }

  function _transformShiftToTemplate(shift) {
    return {
      ...shift,
      locationId: shift.location?.id,
      positionId: shift.position?.id,
      tagIds: shift.tags && shift.tags.map((t) => t.id),
      startTime: utils.formatTime(shift.dtstart, false, true),
      endTime: utils.formatTime(shift.dtend, false, true),
      location: undefined,
      position: undefined,
      tags: undefined,
      dtstart: undefined,
      dtend: undefined,
    };
  }

  function _transformTemplateToShift(template) {
    const tagsDict = context.tags.reduce((agg, t) => ((agg[t.id] = t), agg), {});
    const nowDateString = utils.formatDate(utils.getNowDate());
    const shift = {
      ...template,
      location: { id: template.locationId },
      position: { id: template.positionId },
      tags:
        (context.isOrgFeatureEnabled('tags') &&
          template.tagIds &&
          template.tagIds.map((tagId) => tagsDict[tagId] || { id: tagId })) ||
        [],
      dtstart: utils.getRealDate(`${nowDateString} ${template.startTime}`),
      dtend: utils.getRealDate(`${nowDateString} ${template.endTime}`),
      locationId: undefined,
      positionId: undefined,
    };

    delete shift.user;

    if (shift.dtstart > shift.dtend) {
      shift.dtend = utils.addDays(shift.dtend, 1);
    }

    shift.duration = utils.subtractDates(shift.dtend, shift.dtstart, 'hours');
    return shift;
  }

  function _getEpochsForRange() {
    const referenceDate = utils.getRealDate('2015-01-05');
    const weeksFromRef = (date) => (date - referenceDate) / 1000 / 3600 / 24 / 7;
    let epochFrom = weeksFromRef(_range.from);
    let epochTo = weeksFromRef(_range.to);

    if (epochTo === Math.floor(epochTo)) {
      epochTo--;
    }

    epochFrom = Math.floor(epochFrom);
    epochTo = Math.floor(epochTo);

    const epochs = [];
    for (let e = epochFrom; e <= epochTo; e++) {
      epochs.push(e);
    }

    return epochs;
  }

  function _joinRoomsWithEpoch(epoch) {
    context.webSocketService.send({
      command: 'join',
      ignore: context.authorizationToken,
      room: `events:${context.orgId}:${epoch}`,
    });

    if (context.me.isManager) {
      context.webSocketService.send({
        command: 'join',
        ignore: context.authorizationToken,
        room: `planning:${context.me.id}:${epoch}`,
      });
    }
  }

  function _joinOrUpdateSocketRoom() {
    const epochs = _getEpochsForRange();
    epochs.forEach((epoch) => {
      if (!(epoch in _subscribedEpochs)) {
        _subscribedEpochs[epoch] = true;
        _joinRoomsWithEpoch(epoch);
      }
    });
  }

  async function getEvents(
    fromDateArg,
    toDateArg,
    ignoreLoadingBar,
    filters,
    skipUnscheduled,
    showPlanningEvents,
    showForeignEvents
  ) {
    // so we don't modify the date arguments
    let fromDate = new Date(fromDateArg);
    let toDate = new Date(toDateArg);

    fromDate = utils.setHours(fromDate, 0, 0, 0, 0);
    toDate = utils.setHours(toDate, 0, 0, 0, 0);

    if (context.serverSideFilteringEnabled) {
      _mergeUsers();
      const result = await _loadFromServer({
        fromDate,
        toDate,
        ignoreLoadingBar,
        filters,
        skipUnscheduled,
        showPlanningEvents,
        showForeignEvents,
      });

      _setRange({
        from: fromDate,
        to: toDate,
      });

      return result;
    }

    const shouldClearCache =
      _range.from &&
      _range.to &&
      !utils.doDateRangesOverlaps(_range, { from: fromDate, to: toDate });

    if (shouldClearCache) {
      _resetData();
    }

    // We save the full range of requested events, use that to update the socket room so we
    // get updates for changes in the same full range. So when you first request events
    // from 1-1-2015 to 1-2-2015 and then from 1-2-2015 to 1-3-2015, we save the full 1-1-2015 to 1-3-2015 range.
    const { rangeWasExpanded, oldRange, newRange } = _getExpandedRange(_range, fromDate, toDate);
    const noRangeBefore = !oldRange.from && !oldRange.to;

    _mergeUsers();

    if (rangeWasExpanded) {
      // Make sure there are no gaps in the range. If you first requests shifts from 1-1-2015 to 1-2-2015 and
      // then from 1-3-2015 to 1-4-2015, we have a gap of one month. And since we subscribe to the socket server
      // with a single range, we need to just request the same range from the server.
      // This will probably bite us in the ass soon, if the second range is a year in the future..!

      /*
              _______________ request this _____________
              |                                        |
      ------------------------########### GAP #########-----------------------------
              ^              ^                         ^              ^
          fromDate         toDate                  oldRange.from  oldRange.to
          range.from                                               range.to
      */
      if (oldRange.from && oldRange.from > toDate) {
        if (!isTest) {
          console.log('FIXED GAP');
        }

        toDate = oldRange.from;
      }

      /*
                             _______________ request this _____________
                             |                                        |
      ------------------------########### GAP #########-----------------------------
              ^              ^                         ^              ^
        oldRange.from    oldRange.to                fromDate        toDate
          range.from                                               range.to
      */
      if (oldRange.to && oldRange.to < fromDate) {
        if (!isTest) {
          console.log('FIXED GAP');
        }

        fromDate = oldRange.to;
      }

      // We only use the smaller range for actually requesting events. So when you first request events
      // from 1-1-2015 to 1-2-2015 and then from 1-2-2015 to 1-3-2015, we only fetch from 1-2-2015 to 1-3-2015.
      const result = await _loadFromServer({
        fromDate,
        toDate,
        ignoreLoadingBar,
        noRangeBefore,
      });
      _setRange(newRange);
      return result;
    }

    if (context.shouldProcessServerLaborData) {
      const eventsWithoutCost = instance.allEvents.filter(
        (event) => event.type === SHIFT_TYPE && event.user && !(event.id in _shiftsCosts)
      );

      if (eventsWithoutCost.length) {
        const {
          costsArrays: costs,
          stale,
          salaries,
        } = await getShiftsCosts(
          fromDate,
          toDate,
          eventsWithoutCost.map((e) => e.id)
        );
        instance.staleLaborData = stale;
        Object.assign(_shiftsCosts, costs);

        eventsWithoutCost.forEach((event) => {
          if (!(event.id in _shiftsCosts)) {
            _shiftsCosts[event.id] = null;
          }
        });

        updateSalaryCosts(salaries, fromDate, toDate);
        storeServerSideShiftsLaborData(instance.allEvents);
        await cacheContext.syncWorker({ changed: instance.allEvents });
      }
    }

    return false;
  }

  function updateSalaryCosts(salaries, fromDate, toDate) {
    Object.assign(instance.salaryCosts, salaries);
    utils
      .getDatesBetween(fromDate, toDate)
      .map(utils.formatDate)
      .forEach((dateString) => {
        if (!(dateString in salaries)) {
          delete instance.salaryCosts[dateString];
        }
      });
  }

  function getShiftsCosts(fromDateArg, toDateArg, ids = []) {
    let fromDate = new Date(fromDateArg);
    let toDate = new Date(toDateArg);

    fromDate = utils.setHours(fromDate, 0, 0, 0, 0);
    toDate = utils.setHours(toDate, 0, 0, 0, 0);

    const datesParam = utils.getDatesRangeParam(fromDate, toDate);

    return cacheContext.httpPost(
      'labor/cost',
      ids.length ? { ids } : {},
      { dates: datesParam },
      {}, // httpConfig
      {
        'X-HTTP-Method-Override': 'GET',
      }
    );
  }

  function onChange(handler) {
    _onChangeHandlers.push(handler);

    return () => {
      const index = _onChangeHandlers.indexOf(handler);
      _onChangeHandlers.splice(index, 1);
    };
  }

  async function addOrEditEvents(eventArray = [], { noNotify = false } = {}) {
    storeServerSideShiftsLaborData(eventArray);

    if (!isTest) {
      console.log('addOrEditEvents', eventArray);
    }

    if (!instance.allEvents) {
      instance.allEvents = [];
    }

    if (eventArray.length) {
      eventArray.forEach((event) => {
        if (event.status === 'cancelled') {
          // Remove from the collection
          instance.allEvents = instance.allEvents.filter((e) => e.id !== event.id);
          return;
        }

        delete _logCache[event.id];
        if (context.isTimeOff(event.type)) {
          Object.keys(_leaveStatusCache).forEach((year) => {
            delete _leaveStatusCache[year][event.user.id];
          });
        }

        const existingEvent = instance.allEvents.find((e) => e.id === event.id);

        if (existingEvent) {
          // Update

          // need to capture timesheets because they do not exist on every event response (yet)
          const { timesheets } = existingEvent;
          utils.clearObject(existingEvent);
          Object.assign(existingEvent, event);

          if (timesheets && !('timesheets' in event)) {
            existingEvent.timesheets = timesheets;
          }
        } else {
          // Insert
          instance.allEvents.push(event);
        }

        if (cacheContext.onEventUpdated) {
          cacheContext.onEventUpdated(event);
        }
      });

      if (!noNotify) {
        _notifyOnChange(eventArray);
      }
    }

    await cacheContext.syncWorker({ changed: eventArray });
  }

  async function deleteEvent(event) {
    if (!isTest) {
      console.log('deleteEvent', event);
    }

    _leaveStatusCache = {};
    if (instance.allEvents) {
      instance.allEvents = instance.allEvents.filter((e) => e.id !== event.id);
    }

    _notifyOnChange();
    await cacheContext.syncWorker({ deleted: [event] });
  }

  function addOrEditNote(note) {
    if (!isTest) {
      console.log('addOrEditNote', note);
    }

    if (instance.allEvents && note) {
      const existingNote = instance.notes.find((n) => n.id === note.id);

      if (existingNote) {
        // Update
        Object.assign(existingNote, note);
      } else {
        // Insert
        instance.notes.push(note);
      }

      _notesDict = null;
    }
  }

  function deleteNote(note) {
    if (instance.allEvents && note && instance.notes.length) {
      instance.notes = instance.notes.filter((n) => n.id !== note.id);
      _notesDict = null;
    }
  }

  function getLogs(shiftId) {
    if (_logCache[shiftId]) {
      return _logCache[shiftId];
    }

    const promise = cacheContext.httpGet(`calendar/${encodeURIComponent(shiftId)}/logs`);
    _logCache[shiftId] = promise;
    return promise;
  }

  async function addShift(shift, paramsArg = {}, httpConfig = {}) {
    const params = _extendParamsWithDates(paramsArg);

    if (context.consecutiveWorkDaysLimitEnabled) {
      params.checkConsecutiveWorkDaysConflicts = true;
    }

    const result = await cacheContext.httpPost(
      'shifts',
      _transformShiftToServer(shift, params.publish || false),
      params,
      httpConfig
    );

    if (!httpConfig.noPostProcess) {
      await _addOrEditResponse(result);
    }

    instance.undoingPossible = !paramsArg.publish;
    _redoTransactionId = false;

    return result;
  }

  async function addTimeoff(timeoff, paramsArg = {}, httpConfig = {}) {
    const params = _extendParamsWithDates(paramsArg);
    const result = await cacheContext.httpPost(
      'leave',
      _transformLeaveToServer(timeoff),
      params,
      httpConfig
    );

    if (!httpConfig.noPostProcess) {
      await _addOrEditResponse(result);
    }

    return result;
  }

  function getShiftDiff(reference, shift) {
    const diff = {};

    if (!('user' in reference) && shift.user) {
      diff.user = shift.user;
    }

    const match = (key) => {
      switch (key) {
        case 'dtstart':
        case 'dtend':
          return reference[key].toString().substr(0, 16) === shift[key].toString().substr(0, 16);
        case 'position':
        case 'location':
        case 'user':
          return (
            (!reference[key] && !shift[key]) ||
            (reference[key] && shift[key] && reference[key].id === shift[key].id)
          );
        case 'fullDay':
          return Boolean(reference[key]) === Boolean(shift[key]);
        case 'tags':
        case 'users':
          return utils.areSetsEqual(
            new Set((reference[key] || []).map((item) => item.id)),
            new Set((shift[key] || []).map((item) => item.id))
          );
        default:
          return reference[key] === shift[key];
      }
    };

    Object.keys(reference).forEach((key) => {
      if (!match(key)) {
        diff[key] = shift[key];
      }
    });

    return diff;
  }

  function _shouldIgnoreConflicts(shift, transformedToServer) {
    if (shift.users && shift.users.length > 1) {
      return false;
    }

    const referenceShift = instance.allEvents && instance.allEvents.find((e) => e.id === shift.id);
    if (!referenceShift) {
      return false;
    }

    const diff = getShiftDiff(referenceShift, transformedToServer);

    /**
     * If the only things that changed are status and summary we should skip conflicts checking
     */
    return Object.keys(diff).every((diffKey) => ['status', 'summary'].includes(diffKey));
  }

  async function updateShift(shift, paramsArg = {}, httpConfig = {}) {
    const params = _extendParamsWithDates(paramsArg);
    const transformed = _transformShiftToServer(shift, params.publish);

    if (_shouldIgnoreConflicts(shift, transformed)) {
      params.ignoreConflicts = true;
    }

    if (context.consecutiveWorkDaysLimitEnabled) {
      params.checkConsecutiveWorkDaysConflicts = true;
    }

    const updateResult = await cacheContext.httpPut(
      `shifts/${encodeURIComponent(shift.id)}`,
      transformed,
      params,
      httpConfig
    );

    if (!httpConfig.noPostProcess) {
      await _addOrEditResponse(updateResult);
    }

    if (shift.users) {
      const [, ...newAssignees] = shift.users;

      if (newAssignees.length) {
        const addResult = await instance.addShift(
          {
            ...removeDeletedTaskTemplates(shift),
            copyTaskInstancesFromShift: shift.id,
            users: newAssignees,
            id: null,
            rrule: params.modify === 'future' ? shift.rrule : null,
          },
          params,
          httpConfig
        );
        return [...updateResult, ...addResult];
      }
    }

    if (!paramsArg.publish) {
      instance.undoingPossible = true;
    }

    return updateResult;
  }

  async function shiftsBulk(shifts, paramsArg = {}, httpConfig = {}) {
    const params = httpConfig.skipDates ? paramsArg : _extendParamsWithDates(paramsArg);

    if (context.consecutiveWorkDaysLimitEnabled) {
      params.checkConsecutiveWorkDaysConflicts = true;
    }

    const result = await cacheContext.httpPost(
      'shifts/bulk',
      shifts.map((s) => _transformShiftToServer(s, paramsArg.publish)),
      params,
      httpConfig
    );

    if (!httpConfig.noPostProcess) {
      await _addOrEditResponse(result);
    }

    if (!paramsArg.publish) {
      instance.undoingPossible = true;
    }

    return result;
  }

  async function updateTimeoff(timeoff, paramsArg = {}, httpConfig = {}) {
    const params = _extendParamsWithDates(paramsArg);
    const result = await cacheContext.httpPut(
      `leave/${timeoff.id}`,
      _transformLeaveToServer(timeoff),
      params,
      httpConfig
    );

    if (!httpConfig.noPostProcess) {
      await _addOrEditResponse(result);
    }

    return result;
  }

  function applyForShift(shiftId, paramsArg = {}, httpConfig) {
    const params = _extendParamsWithDates(paramsArg);

    if (context.consecutiveWorkDaysLimitEnabled) {
      params.checkConsecutiveWorkDaysConflicts = true;
    }

    return cacheContext
      .httpPost(`shifts/${encodeURIComponent(shiftId)}/applications`, undefined, params, httpConfig)
      .then(_addOrEditResponse);
  }

  async function deleteShift(shift, paramsArg = {}, httpConfig) {
    const params = _extendParamsWithDates(paramsArg);
    await cacheContext.httpDelete(`shifts/${encodeURIComponent(shift.id)}`, params, httpConfig);
    let deletedEvents;

    function mapCancelledEvent(event) {
      return {
        id: event.id,
        status: 'cancelled',
      };
    }

    if (params.modify === 'future' && shift.rrule && instance.allEvents) {
      deletedEvents = instance.allEvents
        .filter(
          (event) =>
            event.rrule &&
            event.rrule.id === shift.rrule.id &&
            utils.getRealDate(event.dtstart) >= utils.getRealDate(shift.dtstart)
        )
        .map(mapCancelledEvent);
    } else {
      deletedEvents = [mapCancelledEvent(shift)];
    }

    await instance.addOrEditEvents(deletedEvents);
    checkUndoRedo(); // async, no need to await on that here
    return deletedEvents;
  }

  async function deleteShifts(data, paramsArg = {}, httpConfig) {
    const params = _extendParamsWithDates(paramsArg);
    await cacheContext.httpPost('shifts/delete', data, params, httpConfig);

    const deletedEvents = data.map((event) => ({ ...event, status: 'cancelled' }));
    await instance.addOrEditEvents(deletedEvents);

    checkUndoRedo(); // async, no need to await on that here
    return deletedEvents;
  }

  function publishShift(shiftId, paramsArg = {}, httpConfig) {
    const params = _extendParamsWithDates(paramsArg);
    return cacheContext
      .httpPost(`shifts/${encodeURIComponent(shiftId)}/sync`, undefined, params, httpConfig)
      .then(_addOrEditResponse);
  }

  async function publishShifts(shiftIds, paramsArg = {}, httpConfig) {
    const payload = shiftIds && shiftIds.map((id) => ({ id }));
    const params = _extendParamsWithDates(paramsArg);
    const result = await cacheContext
      .httpPost('shifts/sync', payload, params, httpConfig)
      .then(_addOrEditResponse);
    instance.undoingPossible = false;
    return result;
  }

  function offerShift(shiftId, data, paramsArg = {}, httpConfig) {
    const params = _extendParamsWithDates(paramsArg);

    if (context.consecutiveWorkDaysLimitEnabled) {
      params.checkConsecutiveWorkDaysConflicts = true;
    }

    return cacheContext
      .httpPost(`shifts/${encodeURIComponent(shiftId)}/offer`, data, params, httpConfig)
      .then(_addOrEditResponse);
  }

  function cancelShiftAvailability(shiftId, paramsArg = {}, httpConfig) {
    const params = _extendParamsWithDates(paramsArg);
    return cacheContext
      .httpPost(
        `shifts/${encodeURIComponent(shiftId)}/available/cancel`,
        undefined,
        params,
        httpConfig
      )
      .then(_addOrEditResponse);
  }

  function getShiftCoworkers(shiftId, params = {}, httpConfig) {
    return cacheContext.httpGet(
      `shifts/${encodeURIComponent(shiftId)}/coworkers`,
      params,
      httpConfig
    );
  }

  function getShiftDetails(shiftId, params = {}, httpConfig) {
    return cacheContext.httpGet(
      `shifts/${encodeURIComponent(shiftId)}/detailed`,
      params,
      httpConfig
    );
  }

  function makeShiftAvailable(
    shiftId,
    availableTo = [],
    slots = undefined,
    paramsArg = {},
    httpConfig
  ) {
    const params = _extendParamsWithDates(paramsArg);

    if (context.consecutiveWorkDaysLimitEnabled) {
      params.checkConsecutiveWorkDaysConflicts = true;
    }

    const body = {};
    if (availableTo && availableTo.length) {
      body.availableTo = availableTo;
    }

    if (slots) {
      body.slots = slots;
    }

    return cacheContext
      .httpPost(`shifts/${encodeURIComponent(shiftId)}/available`, body, params, httpConfig)
      .then(_addOrEditResponse);
  }

  function makeShiftsAvailable(data, paramsArg = {}, httpConfig) {
    const params = _extendParamsWithDates(paramsArg);

    if (context.consecutiveWorkDaysLimitEnabled) {
      params.checkConsecutiveWorkDaysConflicts = true;
    }

    return cacheContext
      .httpPost('shifts/available', data, params, httpConfig)
      .then(_addOrEditResponse);
  }

  function acceptShiftOffer(shiftId, paramsArg = {}, httpConfig) {
    const params = _extendParamsWithDates(paramsArg);

    if (context.consecutiveWorkDaysLimitEnabled) {
      params.checkConsecutiveWorkDaysConflicts = true;
    }

    return cacheContext
      .httpPost(`shifts/${encodeURIComponent(shiftId)}/offer/accept`, undefined, params, httpConfig)
      .then(_addOrEditResponse);
  }

  function rejectShiftOffer(shiftId, paramsArg = {}, httpConfig) {
    const params = _extendParamsWithDates(paramsArg);
    return cacheContext
      .httpPost(`shifts/${encodeURIComponent(shiftId)}/offer/reject`, undefined, params, httpConfig)
      .then(_addOrEditResponse);
  }

  function approveShiftOffer(shiftId, paramsArg = {}, httpConfig) {
    const params = _extendParamsWithDates(paramsArg);

    if (context.consecutiveWorkDaysLimitEnabled) {
      params.checkConsecutiveWorkDaysConflicts = true;
    }

    return cacheContext
      .httpPost(
        `shifts/${encodeURIComponent(shiftId)}/offer/approve`,
        undefined,
        params,
        httpConfig
      )
      .then(_addOrEditResponse);
  }

  function retractShiftOffer(shiftId, paramsArg = {}, httpConfig) {
    const params = _extendParamsWithDates(paramsArg);
    return cacheContext
      .httpPost(
        `shifts/${encodeURIComponent(shiftId)}/offer/retract`,
        undefined,
        params,
        httpConfig
      )
      .then(_addOrEditResponse);
  }

  function cancelShiftOffer(shiftId, paramsArg = {}, httpConfig) {
    const params = _extendParamsWithDates(paramsArg);
    return cacheContext
      .httpPost(`shifts/${encodeURIComponent(shiftId)}/offer/cancel`, undefined, params, httpConfig)
      .then(_addOrEditResponse);
  }

  function denyShiftApplicant(shiftId, applicantId, paramsArg = {}, httpConfig) {
    const params = _extendParamsWithDates(paramsArg);
    return cacheContext
      .httpPost(
        `shifts/${encodeURIComponent(shiftId)}/applications/${applicantId}/deny`,
        undefined,
        params,
        httpConfig
      )
      .then(_addOrEditResponse);
  }

  function denyShiftApplicants(shiftId, applicantIds, paramsArg = {}, httpConfig) {
    const params = _extendParamsWithDates(paramsArg);
    return cacheContext
      .httpPost(
        `shifts/${encodeURIComponent(shiftId)}/applications/deny`,
        applicantIds,
        params,
        httpConfig
      )
      .then(_addOrEditResponse);
  }

  function approveShiftApplicant(shiftId, applicantId, keepOpen, paramsArg = {}, httpConfig) {
    const params = _extendParamsWithDates(paramsArg);

    if (context.consecutiveWorkDaysLimitEnabled) {
      params.checkConsecutiveWorkDaysConflicts = true;
    }

    return keepOpen
      ? cacheContext
          .httpPost(
            `shifts/${encodeURIComponent(shiftId)}/applications/${applicantId}/assign`,
            undefined,
            params,
            httpConfig
          )
          .then(_addOrEditResponse)
      : cacheContext
          .httpPost(
            `shifts/${encodeURIComponent(shiftId)}/applications/${applicantId}/approve`,
            undefined,
            params,
            httpConfig
          )
          .then(_addOrEditResponse);
  }

  function approveShiftApplications(shiftId, applicationIds, paramsArg = {}, httpConfig) {
    const params = _extendParamsWithDates(paramsArg);

    if (context.consecutiveWorkDaysLimitEnabled) {
      params.checkConsecutiveWorkDaysConflicts = true;
    }

    return cacheContext
      .httpPost(
        `shifts/${encodeURIComponent(shiftId)}/applications/approve`,
        applicationIds,
        params,
        httpConfig
      )
      .then(_addOrEditResponse);
  }

  function retractShiftApplication(shiftId, applicationId, paramsArg = {}, httpConfig) {
    const params = _extendParamsWithDates(paramsArg);
    return cacheContext
      .httpPost(
        `shifts/${encodeURIComponent(shiftId)}/applications/${applicationId}/retract`,
        undefined,
        params,
        httpConfig
      )
      .then(_addOrEditResponse);
  }

  async function copyShifts(data, paramsArg = {}, httpConfig) {
    const params = _extendParamsWithDates(paramsArg);

    if (context.consecutiveWorkDaysLimitEnabled) {
      params.checkConsecutiveWorkDaysConflicts = true;
    }

    const result = await cacheContext
      .httpPost('shifts/copy', data, params, httpConfig)
      .then(_addOrEditResponse);
    instance.undoingPossible = true;
    return result;
  }

  function checkDirty(paramsArg = {}, httpConfig) {
    const params = _extendParamsWithDates(paramsArg);
    return cacheContext.httpGet('shifts/sync', params, httpConfig);
  }

  async function checkUndoRedo(paramsArg = {}, httpConfig) {
    const params = _extendParamsWithDates(paramsArg);
    const data = await cacheContext.httpGet('shifts/transactions', params, httpConfig);
    instance.undoingPossible = data.canUndo;
    _redoTransactionId = data.redoId;
    return data;
  }

  async function undoChanges(paramsArg = {}, httpConfig) {
    const params = _extendParamsWithDates(paramsArg);
    const result = await cacheContext
      .httpDelete('shifts/sync', params, httpConfig)
      .then(_addOrEditResponse);
    checkUndoRedo();
    return result;
  }

  async function redoChanges(transactionId, paramsArg = {}, httpConfig) {
    const params = _extendParamsWithDates(paramsArg);
    const result = await cacheContext
      .httpPost(`shifts/transactions/${transactionId}`, undefined, params, httpConfig)
      .then(_addOrEditResponse);
    checkUndoRedo();
    return result;
  }

  async function getNextShifts(params = {}, httpConfig) {
    const response = await cacheContext.httpGet('shifts/next', params, httpConfig);
    const shifts = response.id ? [response] : [];
    return await _addOrEditResponse(shifts);
  }

  async function getCurrentShifts(params = {}, httpConfig) {
    if (!instance.currentShifts) {
      instance.currentShifts = await cacheContext.httpGet('shifts/current', params, httpConfig);
    }

    return instance.currentShifts;
  }

  function getClockIn(rawResponse = false, params = {}, httpConfig) {
    const promise = cacheContext.httpGet('shifts/clockin', params, httpConfig);
    return rawResponse ? promise : promise.then(_addOrEditResponse);
  }

  function getLastClockIn(httpConfig) {
    return cacheContext.httpGet('timeclock/clockin', {}, httpConfig);
  }

  function clockIn(coords, rawResponse = false, params = {}, httpConfig) {
    const promise = cacheContext.httpPost('timeclock/clockin', coords, params, httpConfig);
    promise.then((shift) => updateCurrentShifts([shift]));
    return rawResponse ? promise : promise.then(_addOrEditResponse);
  }

  function clockOut(coords, rawResponse = false, params = {}, httpConfig) {
    const promise = cacheContext.httpPost('timeclock/clockout', coords, params, httpConfig);
    promise.then(() => updateCurrentShifts(null));
    return rawResponse ? promise : promise.then(_addOrEditResponse);
  }

  function takeBreak(rawResponse = false, params = {}, httpConfig) {
    const promise = cacheContext.httpPost('timeclock/break', {}, params, httpConfig);
    promise.then((shift) => updateCurrentShifts([shift]));
    return rawResponse ? promise : promise.then(_addOrEditResponse);
  }

  function endBreak(rawResponse = false, params = {}, httpConfig) {
    const promise = cacheContext.httpDelete('timeclock/break', params, httpConfig);
    promise.then((shift) => updateCurrentShifts([shift]));
    return rawResponse ? promise : promise.then(_addOrEditResponse);
  }

  function getEvent(id, params = {}, httpConfig) {
    return cacheContext
      .httpGet(`shifts/${encodeURIComponent(id)}`, params, httpConfig)
      .then(_addOrEditResponse);
  }

  function getTimesheetEvent(shiftId, userId, params = {}, httpConfig) {
    return cacheContext.httpGet(
      `timesheets/event/${encodeURIComponent(shiftId)}/${encodeURIComponent(userId)}`,
      params,
      httpConfig
    );
  }

  function saveTimeOff(_timeOff, params = {}, httpConfig) {
    const timeOff = { ..._timeOff };
    const dtstart = utils.getRealDate(timeOff.dtstart);
    const dtend = utils.getRealDate(timeOff.dtend);
    // Send timezone-less times!
    timeOff.dtstart = `${utils.formatDate(dtstart)}T${utils.formatTime(dtstart, false, true)}`;
    timeOff.dtend = `${utils.formatDate(dtend)}T${utils.formatTime(dtend, false, true)}`;

    if (timeOff.id) {
      return cacheContext
        .httpPut(`leave/${timeOff.id}`, timeOff, params, httpConfig)
        .then(_addOrEditResponse);
    }

    return cacheContext.httpPost('leave', timeOff, params, httpConfig).then(_addOrEditResponse);
  }

  async function deleteTimeOff(timeOff, params = {}, httpConfig) {
    const deletedTimeOff = await cacheContext.httpDelete(`leave/${timeOff.id}`, params, httpConfig);
    await deleteEvent(deletedTimeOff);
    return deletedTimeOff;
  }

  function approveTimeOff(timeOff, params = {}, httpConfig) {
    return cacheContext
      .httpPost(`leave/${timeOff.id}/approve`, {}, params, httpConfig)
      .then(_addOrEditResponse);
  }

  function denyTimeOff(timeOff, params = {}, httpConfig) {
    return cacheContext
      .httpPost(`leave/${timeOff.id}/deny`, {}, params, httpConfig)
      .then(_addOrEditResponse);
  }

  async function getTimeOffTypes(force = false) {
    if (force || instance.timeOffTypes || !instance.timeOffTypes.length) {
      instance.timeOffTypes = await loadTimeOffTypes();
    }
    return instance.timeOffTypes;
  }

  function loadTimeOffTypes(params = {}, httpConfig) {
    if (context.isPrefabTimeoffDisabled) {
      return Promise.resolve([
        {
          available: true,
          paid: true,
          name: gettext('Leave'),
          key: 'leave',
        },
      ]);
    }

    return cacheContext.httpGet('leave/types', params, httpConfig);
  }

  function getUserLeavesStatus(userId, params = {}, httpConfig) {
    const year = params.year || new Date().getFullYear();
    if (!(year in _leaveStatusCache)) {
      _leaveStatusCache[year] = {};
    }

    if (userId in _leaveStatusCache[year]) {
      return _leaveStatusCache[year][userId];
    }

    const leaveStatusPromise = cacheContext.httpGet(
      `reports/leave/users/${userId}/summary`,
      params,
      httpConfig
    );
    return (_leaveStatusCache[year][userId] = leaveStatusPromise);
  }

  function getUserCaps(userId, params = {}, httpConfig) {
    if (userId in _userLeaveCapsCache) {
      return _userLeaveCapsCache[userId];
    }

    const leaveStatusPromise = cacheContext.httpGet(`leave/caps/${userId}`, params, httpConfig);
    return (_userLeaveCapsCache[userId] = leaveStatusPromise);
  }

  async function updateUserCaps(userId, payload, params = {}, httpConfig) {
    await cacheContext.httpPut(`leave/caps/${userId}/bulk`, payload, params, httpConfig);
    delete _userLeaveCapsCache[userId];
  }

  function getNoShowSummary(userId, params = {}, httpConfig) {
    return cacheContext.httpGet(`reports/noshows/users/${userId}/summary`, params, httpConfig);
  }

  async function saveTimeOffTypes(types, params = {}, httpConfig) {
    instance.timeOffTypes = await cacheContext.httpPut(
      'leave/types/bulk',
      types,
      params,
      httpConfig
    );
    return instance.timeOffTypes;
  }

  function setContext(newContext) {
    Object.assign(cacheContext, newContext);
  }

  function updateCurrentShifts(currentShifts) {
    instance.currentShifts = currentShifts;
    _onCurrentShiftsChangeHandlers.forEach((h) => h(instance.currentShifts));
  }

  function onCurrentShiftsChange(handler) {
    _onCurrentShiftsChangeHandlers.push(handler);

    return () => {
      const index = _onCurrentShiftsChangeHandlers.findIndex((h) => h === handler);
      if (index >= 0) {
        _onCurrentShiftsChangeHandlers.splice(index, 1);
      }
    };
  }

  async function addNoShow(noShow, params = {}, httpConfig = {}) {
    const { shiftId, summary, shiftAction = '', shiftRecipientId } = noShow;
    const noShowBody = {
      shiftId,
      summary,
    };

    if (shiftAction) {
      noShowBody.shiftAction = shiftAction;
      noShowBody.shiftRecipientId = shiftRecipientId || '';
    }

    const result = await cacheContext.httpPost('noshows', noShowBody, params, httpConfig);
    await _addOrEditResponse(result);

    return result;
  }

  async function deleteNoShow(noShowId, params = {}, httpConfig = {}) {
    const result = await cacheContext.httpDelete(`noshows/${noShowId}`, params, httpConfig);
    await _addOrEditResponse(result);

    return result;
  }

  async function addSickCallout(sickCallout, params = {}, httpConfig = {}) {
    const {
      shiftId,
      rangeStart,
      rangeEnd,
      userId,
      summary,
      shiftAction = '',
      shiftRecipientId,
    } = sickCallout;

    const sickCalloutBody = {
      shiftId,
      rangeStart,
      rangeEnd,
      userId,
      summary,
    };

    if ('paid' in sickCallout) {
      sickCalloutBody.paid = sickCallout.paid;
    }

    if (shiftAction) {
      sickCalloutBody.shiftAction = shiftAction;
      sickCalloutBody.shiftRecipientId = shiftRecipientId || '';
    }

    const result = await cacheContext.httpPost('sick_callout', sickCalloutBody, params, httpConfig);
    await _addOrEditResponse(result);

    return result;
  }

  function getSickCalloutSummary(userId, params = {}, httpConfig) {
    return cacheContext.httpGet(
      `reports/sick_callouts/users/${userId}/summary`,
      params,
      httpConfig
    );
  }

  async function deleteSickCallout(sickCalloutId, params = {}, httpConfig = {}) {
    const result = await cacheContext.httpDelete(
      `sick_callout/${sickCalloutId}`,
      params,
      httpConfig
    );
    await _addOrEditResponse(result);

    return result;
  }

  async function markDay(type, event, params = {}, httpConfig = {}) {
    const result = await cacheContext.httpPost(`day_mark/${type}`, event, params, httpConfig);
    await _addOrEditResponse(result);

    return result;
  }

  async function unmarkDay(type, eventId, params = {}, httpConfig = {}) {
    const result = await cacheContext.httpDelete(`day_mark/${type}/${eventId}`, params, httpConfig);
    await deleteEvent(result);

    return result;
  }

  async function markDayBulk(payload, params = {}, httpConfig = {}) {
    const result = await cacheContext.httpPost('day_mark/bulk', payload, params, httpConfig);
    await _addOrEditResponse(result);

    return result;
  }

  async function loadShiftsAvailableForSwap(shiftId, params = {}, httpConfig = {}) {
    const result = await cacheContext.httpGet(
      `shifts/exchange/available/${shiftId}`,
      params,
      httpConfig
    );
    return parseUsersShifts(result);
  }

  function parseUsersShifts(usersShifts) {
    return Object.keys(usersShifts).reduce((acc, userId) => {
      const shifts = usersShifts[userId].map((shift) => extendShiftModel(shift));
      acc[userId] = shifts;
      return acc;
    }, {});
  }

  async function initiateSwap(
    { offererId, offererShiftId, offereeId, offereeShiftId },
    params = {},
    httpConfig = {}
  ) {
    const result = await cacheContext.httpPost(
      'shifts/exchange',
      { offererId, offererShiftId, offereeId, offereeShiftId },
      params,
      httpConfig
    );
    const updatedShifts = [result.offererShift, result.offereeShift];
    await _addOrEditResponse(updatedShifts);
    return result;
  }

  async function loadSwapDetails(swapId, params = {}, httpConfig = {}) {
    return await cacheContext.httpGet(`shifts/exchange/${swapId}`, params, httpConfig);
  }

  async function cancelShiftSwap(swapId, params = {}, httpConfig = {}) {
    const result = await cacheContext.httpPost(
      `shifts/exchange/${swapId}/cancel`,
      params,
      httpConfig
    );
    const updatedShifts = [result.offererShift, result.offereeShift];
    await _addOrEditResponse(updatedShifts);
    return result;
  }

  async function approveShiftSwap(swapId, params = {}, httpConfig = {}) {
    if (context.consecutiveWorkDaysLimitEnabled) {
      params.checkConsecutiveWorkDaysConflicts = true;
    }

    const result = await cacheContext.httpPost(
      `shifts/exchange/${swapId}/approve`,
      null,
      params,
      httpConfig
    );
    const updatedShifts = [result.offererShift, result.offereeShift];
    await _addOrEditResponse(updatedShifts);
    return result;
  }

  async function addBusinessClosedEvent(businessClosedEvent, params = {}, httpConfig = {}) {
    return await markDay('business_closed', businessClosedEvent, params, httpConfig);
  }

  async function deleteBusinessClosedEvent(businessClosedEventId, params = {}, httpConfig = {}) {
    return await unmarkDay('business_closed', businessClosedEventId, params, httpConfig);
  }

  async function addShiftTemplate(template, params = {}, httpConfig = {}) {
    const response = await cacheContext.httpPost(
      'shift/templates',
      _transformShiftToTemplate(template),
      params,
      httpConfig
    );
    const shiftTemplate = _transformTemplateToShift(response);
    instance.shiftTemplates.push(shiftTemplate);
    return shiftTemplate;
  }

  function getShiftTemplates(params = {}, httpConfig = {}) {
    if (!_shiftTempaltesPromise) {
      _shiftTempaltesPromise = cacheContext
        .httpGet('shift/templates', params, httpConfig)
        .then((templates) => templates.map(_transformTemplateToShift));

      _shiftTempaltesPromise.then((templates) => (instance.shiftTemplates = templates));
    }

    return _shiftTempaltesPromise;
  }

  async function updateShiftTemplate(template, params = {}, httpConfig = {}) {
    const response = await cacheContext.httpPut(
      `shift/templates/${template.id}`,
      _transformShiftToTemplate(template),
      params,
      httpConfig
    );
    const shiftTemplate = _transformTemplateToShift(response);
    const existing = instance.shiftTemplates.find((t) => t.id === template.id);
    if (existing) {
      Object.assign(existing, shiftTemplate);
    }

    return shiftTemplate;
  }

  async function deleteShiftTemplate(templateId, params = {}, httpConfig = {}) {
    await cacheContext.httpDelete(`shift/templates/${templateId}`, params, httpConfig);
    instance.shiftTemplates = instance.shiftTemplates.filter((t) => t.id !== templateId);

    if (_shiftTempaltesPromise) {
      _shiftTempaltesPromise = new Promise((resolve) => resolve(instance.shiftTemplates));
    }
  }

  function syncShiftTemplates() {
    _shiftTempaltesPromise = null;
    instance.shiftTemplates = [];
  }

  function updateCalendarSocketRoom(leave = false, rejoin = false) {
    if (leave) {
      if (_calendarSocketRoom) {
        _sendCalendarSocketCommand(_calendarSocketRoom, 'leave');
      }

      return;
    }

    const roomType = 'scratchpad';
    const room = `${roomType}:${context.orgId}`;

    if (!_calendarSocketRoom) {
      _sendCalendarSocketCommand(room, 'join');
      _calendarSocketRoom = room;
    } else if (_calendarSocketRoom !== room || rejoin) {
      _sendCalendarSocketCommand(_calendarSocketRoom, 'leave');
      _sendCalendarSocketCommand(room, 'join');
      _calendarSocketRoom = room;
    }
  }

  function _sendCalendarSocketCommand(room, command) {
    const payload = {
      command,
      room,
    };

    if (command === 'join') {
      payload.ignore = context.authorizationToken;
    }
    context.webSocketService.send(payload);
  }

  function _getExpandedRange(range, fromDate, toDate) {
    const oldRange = { ...range };
    const newRange = { ...range };
    let rangeWasExpanded = false;

    if (fromDate && (!range.from || fromDate < range.from)) {
      newRange.from = fromDate;
      rangeWasExpanded = true;
    }

    if (toDate && (!range.from || toDate > range.to)) {
      newRange.to = toDate;
      rangeWasExpanded = true;
    }

    return { rangeWasExpanded, oldRange, newRange };
  }

  function _setRange(newRange) {
    Object.assign(_range, newRange);
    _joinOrUpdateSocketRoom();
  }

  function syncEvents() {
    _range.from = null;
    _range.to = null;
    _leaveStatusCache = {};
    _userLeaveCapsCache = {};
    syncShiftTemplates();
    instance.updateCurrentShifts(null);
    if (context.me.isManager) {
      checkUndoRedo();
    }
  }

  function syncLabor() {
    _shiftsCosts = {};
    instance.salaryCosts = {};
  }

  function syncUserLeaveCaps() {
    _userLeaveCapsCache = {};
  }

  async function acceptShift(shift, paramsArg = {}, httpConfig = {}) {
    const result = await cacheContext.httpPost(
      `shifts/${shift.id}/acceptance/accept`,
      undefined,
      paramsArg,
      httpConfig
    );
    if (paramsArg.recurring) {
      instance.clearData();
      return;
    }

    return _addOrEditResponse(result);
  }

  async function rejectShift(shift, reason, paramsArg = {}, httpConfig = {}) {
    const result = await cacheContext.httpPost(
      `shifts/${shift.id}/acceptance/reject`,
      { reason },
      paramsArg,
      httpConfig
    );
    if (paramsArg.recurring) {
      instance.clearData();
      return;
    }

    return _addOrEditResponse(result);
  }

  async function bulkAcceptShifts(shiftIds, paramsArg = {}, httpConfig = {}) {
    const result = await cacheContext.httpPost(
      'shifts/acceptance/bulk/accept',
      { shiftIds },
      paramsArg,
      httpConfig
    );
    await _addOrEditResponse(result);
    return result;
  }

  async function getSummaries(fromDateArg, toDateArg, paramsArg, httpConfig = {}) {
    const fromDate = utils.setHours(new Date(fromDateArg), 0, 0, 0, 0);
    const toDate = utils.setHours(new Date(toDateArg), 0, 0, 0, 0);
    const datesParam = utils.getDatesRangeParam(fromDate, toDate);

    const params = {
      dates: datesParam,
      ...paramsArg,
    };

    const result = await cacheContext.httpGet('calendar/summaries', params, httpConfig);
    instance.allSummaries = result;
    return result;
  }
}
